import { createElement, memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiList, MiStackedTypography } from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { OrderAlerts } from "../../Orders/OrdersList/OrderAlerts/OrderAlerts";
import { OrderCardHomeField } from "../../Orders/OrdersList/OrderCardHomeField/OrderCardHomeField";
import { OrderCardOrderTypeField } from "../../Orders/OrdersList/OrderCardOrderTypeField/OrderCardOrderTypeField";
import { OrderForceCloseoutPopper } from "../../Orders/OrdersList/OrderForceCloseoutPopper/OrderForceCloseoutPopper";
import { OrderInfoPopper } from "../../Orders/OrdersList/OrderInfoPopper/OrderInfoPopper";
import {
  ACCOUNTING_DEFAULT_FIELD_WIDTH,
  ACCOUNTING_TABS_NAMES,
} from "../accountingConstants";
import { QuickBooksIcon } from "./QuickBooksIcon";
import { ACCOUNTING_LIST_CONFIG } from "./accountingListConfig";

/**
 * AccountingList component.
 * Displays a list of orderTemplates grouped by submission date, with detailed information, actions, and filtering options.
 *
 * This component uses memoization for optimization and prevents unnecessary re-renders .
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} props.orderTemplates - An array of order objects to display.
 * @returns {JSX.Element} - Rendered OrdersList component.
 */
export const AccountingList = memo(
  ({
    activeTab,
    isLoading,
    search,
    stateManager,
    refresh,
    refreshPagination,
    orderTemplates,
    orderCardConfig,
    orderHoverCardConfig,
    setOrderTemplates,
    getOrderTemplates,
    onRefresh,
  }) => {
    const { classes, theme } = useStyles();

    const orderCardFieldKeys =
      orderCardConfig?.map(({ fieldKey }) => fieldKey) || [];

    // Filter out fields from static config that already exist in orderCardConfig
    const filteredStaticFields =
      ACCOUNTING_LIST_CONFIG[activeTab.value]?.fields?.filter(
        ({ fieldKey }) => !orderCardFieldKeys.includes(fieldKey)
      ) || [];

    const sections = [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "priority",
            maxWidth: 400,
            justifyContent: "flex-end",
            renderCell: (row) => {
              return (
                <Box
                  position="relative"
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  gap={theme.spacing(2)}
                >
                  {activeTab.value === ACCOUNTING_TABS_NAMES.PENDING_INVOICES &&
                    row.hasVoidedInvoiceOrPurchaseOrder && (
                      <Box
                        bgcolor={theme.palette.red.medium}
                        border={`1px solid ${theme.palette.red.main}`}
                        px={1}
                        height={24}
                        margin={`auto ${theme.spacing(2)}`}
                        alignContent="center"
                        borderRadius={1}
                      >
                        <Typography color="white" variant="body2">
                          Voided
                        </Typography>
                      </Box>
                    )}
                  {row.variance && row.variance !== 0 ? (
                    <Box
                      bgcolor={theme.palette.warning.main}
                      border={`1px solid ${theme.palette.warning.dark}`}
                      px={1}
                      height={24}
                      margin={`auto ${theme.spacing(2)}`}
                      alignContent="center"
                      borderRadius={1}
                    >
                      <Typography color="white" variant="body2">
                        Variance
                      </Typography>
                    </Box>
                  ) : null}
                  {orderCardConfig?.map(({ label, fieldKey }) => (
                    <MiStackedTypography
                      key={fieldKey}
                      title={label}
                      className={classes.fieldMargin}
                      titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                      description={parseFieldValue(
                        getNestedValue(row, fieldKey),
                        label.toLowerCase().includes("date")
                          ? "--/--/--"
                          : undefined,
                        undefined,
                        { isCurrency: fieldKey === "variance", currency: "USD" }
                      )}
                      descriptionProps={{
                        width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                      }}
                    />
                  ))}
                  {filteredStaticFields.map(({ label, fieldKey, displayMap }) =>
                    displayMap ? (
                      displayMap[row[fieldKey]]
                    ) : (
                      <MiStackedTypography
                        key={fieldKey}
                        title={label}
                        className={classes.fieldMargin}
                        titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                        description={parseFieldValue(
                          getNestedValue(row, fieldKey),
                          label.toLowerCase().includes("date")
                            ? "--/--/--"
                            : undefined
                        )}
                        descriptionProps={{
                          width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                        }}
                      />
                    )
                  )}
                  <OrderCardOrderTypeField {...row} />
                </Box>
              );
            },
          },
        ],
      },
    ];

    const renderCardFields = ({ home }) => <OrderCardHomeField home={home} />;

    return (
      <MiList
        showGroupCount
        groupedMode={
          ACCOUNTING_LIST_CONFIG[activeTab.value]?.getGroupedCriteria
        }
        viewType="card"
        cardProps={{
          sections:
            ACCOUNTING_LIST_CONFIG[activeTab.value]?.sections || sections,
          isAlert: ACCOUNTING_LIST_CONFIG[activeTab.value]?.isAlert,
          renderTitle: (order) => (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography color="primary.main" fontWeight={600} fontSize={14}>
                {order.home?.address?.addressLine1 || " "}
              </Typography>
              <OrderInfoPopper
                hideEmails
                order={order}
                fieldsConfig={orderHoverCardConfig}
              />
              {order?.forceCompletedForAccountingDate && (
                <OrderForceCloseoutPopper order={order} />
              )}
              {ACCOUNTING_TABS_NAMES.INVOICED === activeTab.value && (
                <QuickBooksIcon order={order} invoices={order.invoices} />
              )}
              {ACCOUNTING_LIST_CONFIG[activeTab.value]?.getAlerts && (
                <OrderAlerts
                  alerts={ACCOUNTING_LIST_CONFIG[activeTab.value]?.getAlerts(
                    order
                  )}
                />
              )}
            </Box>
          ),
          leftCardProps: {
            fieldset: [
              {
                field: "homeId",
                valueGetter: renderCardFields,
              },
            ],
          },
          renderItemActions: (item) =>
            ACCOUNTING_LIST_CONFIG[activeTab.value].ActionsComponent
              ? createElement(
                  ACCOUNTING_LIST_CONFIG[activeTab.value].ActionsComponent ||
                    null,
                  {
                    ...item,
                    order: item,
                    onRefresh,
                    stateManager,
                    getAlerts:
                      ACCOUNTING_LIST_CONFIG[activeTab.value].getAlerts,
                  }
                )
              : undefined,
        }}
        groupedCriteria={
          ACCOUNTING_LIST_CONFIG[activeTab.value].getGroupedCriteria
        }
        data={orderTemplates}
        loading={isLoading}
        refresh={refresh}
        refreshPagination={refreshPagination}
        search={search}
        paginationMode={PAGINATION.SERVER}
        emptyListProps={{
          text: "No Orders found",
        }}
        setData={setOrderTemplates}
        dataProvider={getOrderTemplates}
        defaultPageSize={25}
        groupedTitle={ACCOUNTING_LIST_CONFIG[activeTab.value].getGroupedTitle}
      />
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  fieldMargin: {
    marginRight: theme.spacing(2),
  },
}));
